/**
 * Nutrition Section Transformer
 * Transforms NutritionTabItem from getNutritionSections API to frontend form format
 */

import { NutritionTabItem } from '@/types/Nutrition.types';
import { INutritionForm, INutritionDocument, INutritionFootnoteStatement, INutrientItem } from '@/types/Nutrition.types';
import { parseFootnoteStatement, transformAttachmentsToDocuments } from './nutritionApiTransformer';
import { getColumnsToDeclareTextForFactsPanelFormat } from '@/utils/columnsToDeclareByFactsPanelFormat';
import { roundNumericString } from "@/utils/helper";
import { isFolateNutrient, FOOD_FOLATE_DEFAULT_UNIT } from "@/utils/folateNutrient";
import { normalizePdcaasBlur } from "@/hooks/useNutrition";

/**
 * Transform a single NutritionTabItem to INutritionForm
 * Maps section data to nutrition form structure
 */
export const transformSectionToNutritionForm = (
    section: NutritionTabItem
): INutritionForm => {
    // Parse footnote statements
    const footnoteStatements: INutritionFootnoteStatement[] = [];
    if (section.nutritionCommentsText && section.nutritionCommentsText.length > 0) {
        section.nutritionCommentsText.forEach(item => {
            const parsed = parseFootnoteStatement(item.text);
            if (parsed) {
                footnoteStatements.push(parsed);
            }
        });
    }
    
    // Transform attachments to documents
    const documents = transformAttachmentsToDocuments(section.attachments as any || []);
    
    // Combine serving quantity with UOM
    const servingQuantityForCalculation = section.servingQuantityForCalculations && section.servingQuantityForCalculationsUom
        ? `${section.servingQuantityForCalculations} ${section.servingQuantityForCalculationsUom}`
        : '';
    
    // Combine serving quantity column 2 with UOM
    const servingQuantityColumn2 = section.servingQuantityForCalculationsCol2 && section.servingQuantityForCalculationsCol2Uom
        ? `${section.servingQuantityForCalculationsCol2} ${section.servingQuantityForCalculationsCol2Uom}`
        : '';
    
    // Transform nutrients array
    const nutrientsUnsorted: INutrientItem[] = (section.nutrients || []).map((nutrient: any) => ({
        id: nutrient.id,
        nutrient: nutrient.nutrient || '',
        sortOrder: (() => {
            const raw = nutrient.sortOrder;
            const n = typeof raw === 'number' ? raw : typeof raw === 'string' ? Number(raw) : NaN;
            return Number.isFinite(n) ? n : undefined;
        })(),
        nutritionUnitOfMeasure: nutrient.nutrientUnitOfMeasure || '',
        unroundedPer100gAsSoldColumn1: roundNumericString(nutrient.unroundedPer100gAsSoldCol1),
        unroundedPerServingAsSoldColumn1: roundNumericString(nutrient.unroundedPerServingAsSoldCol1),
        unroundedPer100gAsSoldColumn2: roundNumericString(nutrient.unroundedPer100gAsSoldCol2),
        unroundedPerServingAsSoldColumn2: roundNumericString(nutrient.unroundedPerServingAsSoldCol2),
        guaranteedPercent: nutrient.guaranteedPercent || '',
        valueOnLabCoA: nutrient.valueOnLabCoA || '',
        valueToAppearOnLabel: nutrient.valueToAppearOnLabel || '',
        unitOfMeasure: nutrient.unitOfMeasure || '',
        ...(isFolateNutrient(nutrient.nutrient) && nutrient.foodFolate != null && nutrient.foodFolate !== ''
            ? {
                foodFolate: roundNumericString(nutrient.foodFolate),
                unitOfMeasure: nutrient.unitOfMeasure || FOOD_FOLATE_DEFAULT_UNIT,
            }
            : {}),
    }));
    // Preserve API section nutrient order; table display is ordered by onload list.
    const nutrients = nutrientsUnsorted;
    
    // Map to frontend form format
    const nutritionForm: INutritionForm = {
        // ID from section
        id: section.id,
        
        // Genesis ID from section
        genesisId: section.genesisId || '',
        
        // Validation status (API returns string; coerce to boolean for form)
        isNutritionValidated: section.isNutritionValidated === "true",

        // Section name (user-visible)
        sectionName: section.sectionName || '',
        
        // Product specification name (from vendor assessment)
        productSpecificationName: section.vendorAssessment?.productName || '',
        version: '', // Not provided in section data
        
        // Basic fields
        nutritionDataSubmissionMethod: section.howNutritionDataBeingSubmitted || '',
        factsPanelFormat: section.factsPanelFormatForLabel || '',
        headingText: section.headingText || '',
        footnote: section.footnote || '',
        calorieContent: (section as any).calorieContent || '',
        columnsToDeclare: getColumnsToDeclareTextForFactsPanelFormat(
            section.factsPanelFormatForLabel || ''
        ),
        servingSizeAndHouseholdMeasure: section.servingSizeHouseholdMeasure || '',
        servingsPerContainer: section.servingsPerContainer || '',
        servingQuantityForCalculation: servingQuantityForCalculation,
        dvCalculationBasis: section.percentageDvCalculatedBasedOn || '',
        pdcaas: normalizePdcaasBlur((section as any).pdcaas),
        rdiCalculationBasis: section.percentageRdiCalculatedBasedOn || '',
        calculateAmounts: section.calculateAmounts || '',
        dataSource: section.sourceOfData || '',
        
        // Dual column fields
        column1Heading: section.column1Heading ?? '',
        column2Heading: section.headingTextCol2 || '',
        amountPreparedText: section.amountPreparedText || '',
        servingQuantityColumn2: servingQuantityColumn2,
        
        // Pet food guaranteed analysis
        guaranteedAnalysis: section.guaranteedAnalysis || '',
        
        // Parsed footnote statements
        footnoteAdditionStatements: footnoteStatements,
        
        // Transformed documents
        documents: documents,
        
        // Nutrients from section
        nutrients: nutrients,
        
        // Pack Copy document (if available)
        packCopy: section.nfpLabel ? {
            id: section.nfpLabel.id,
            filename: section.nfpLabel.filename
        } : undefined
    };
    
    return nutritionForm;
};

/**
 * Extract section data (id, name) from NutritionTabItem
 */
export const extractSectionData = (section: NutritionTabItem) => {
    return {
        sectionId: section.id,
        sectionName: section.sectionName,
        nutritionTabId: parseInt(section.id, 10), // Convert string ID to number for API
        nfpLabel: section.nfpLabel || null,
        nfpLabelLastGenerationDateTime: section.nfpLabelLastGenerationDateTime || null
    };
};

